import React from 'react';
import styled from 'styled-components';

// organisms
import MemberContactsLists from '@organisms/MemberContactsLists';
import MemberEventsLists from '@organisms/MemberEventsLists';

// molecules
import ErrorHandler from '../../_molecules/ErrorHandler';
import TrackPageView from '@tracking/TrackPageView';
import SectionHeader from '../../_molecules/SectionHeader';
import SearchArticlesSaved from '../../searches/SearchArticlesSaved';
import HeroWithVideo from '@molecules/HeroWithVideo';
import LoadingArea from '@molecules/LoadingArea';
import MarketingCard from '@molecules/MarketingCard';
import MarketingCards from '@molecules/MarketingCards';
import MemberProfileStatusCard from '@molecules/MemberProfileStatusCard';
import MembershipStatusCard from '@molecules/MembershipStatusCard';

// atoms
import { DivWithShadowAlternate, H4, SectionWrapperInner, SectionWrapperOuter } from '@styled';

// functionality
import useViewer from '@hooks/useViewer';
import useDocumentTitle from '@hooks/useDocumentTitle';

const HeaderContainer = styled.div`
    margin-bottom: 40px;
`;

const Dashboard = () => {
    const { viewer } = useViewer();
    const { documentTitle } = useDocumentTitle('My ICSC Dashboard');

    if (!viewer) {
        return <LoadingArea />;
    }

    return (
        <ErrorHandler>
            <TrackPageView>
                <SectionWrapperOuter>
                    <SectionWrapperInner className="fade-in-fast">
                        <SectionHeader title={documentTitle} />

                        <HeaderContainer>
                            <HeroWithVideo
                                imageSrc="/assets/images/pattern-white.svg"
                                videoSrc="https://vimeo.com/667434195/b25b93e8ec"
                            />
                        </HeaderContainer>

                        <div className="px-3 mt-5 px-md-0">
                            <H4 className="mb-1.5rem">Membership and Profile Status</H4>

                            <div className="flex-wrap row">
                                <div className="col-lg-4 d-flex flex-column mb-4 mb-lg-0">
                                    <MembershipStatusCard />
                                </div>

                                <div className="col-lg-8 d-flex flex-column">
                                    <MemberProfileStatusCard />
                                </div>
                            </div>
                        </div>

                        {viewer.status === 'Active' && (
                            <>
                                <div className="px-3 mt-5 mt-md-5 px-md-0">
                                    <H4 className="mb-3">Upcoming Events</H4>
                                    <MemberEventsLists />
                                </div>

                                <div className="px-3 mt-5 mt-md-5 px-md-0">
                                    <H4 className="mb-3">Connections</H4>
                                    <MemberContactsLists />
                                </div>

                                <div className="px-3 mt-5 mt-md-5 px-md-0">
                                    <H4 className="mb-3">Properties</H4>

                                    <MarketingCards>
                                        <MarketingCard
                                            title="Connect with prospective tenants"
                                            ctaText="Find Tenants"
                                            ctaUrl="/my-icsc/connections/matches"
                                        />

                                        <MarketingCard
                                            title="See what property opportunities exist"
                                            ctaText="Explore Available Properties"
                                            ctaUrl="/my-icsc/properties/find"
                                        />

                                        <MarketingCard
                                            title="Keep your listed properties up to date"
                                            ctaText="Manage Properties"
                                            ctaUrl="/my-icsc/properties/listed"
                                        />
                                    </MarketingCards>
                                </div>

                                <div className="px-3 mt-5 mt-md-5 px-md-0">
                                    <H4 className="mb-3">Saved Articles</H4>

                                    <DivWithShadowAlternate className="p-2rem">
                                        <SearchArticlesSaved
                                            defaultViewType="list"
                                            hitsPerPage={3}
                                            NoResultsText={() => "Looks like you haven't saved any articles."}
                                            showPagingToolbar
                                        />
                                    </DivWithShadowAlternate>
                                </div>
                            </>
                        )}
                    </SectionWrapperInner>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};

export default Dashboard;
